import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import styled from "styled-components";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import Button from "../components/common/Button";
import TextInput from "../components/common/TextInput";
import Meta from "../components/Meta";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";

/* =============================== Styling =============================== */

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    error: "#dc2626",
    errorLight: "#fef2f2",
  },
  shadows: {
    sm: "0 1px 3px rgba(15, 23, 42, 0.06)",
    md: "0 4px 16px rgba(15, 23, 42, 0.08)",
    lg: "0 8px 32px rgba(15, 23, 42, 0.12)",
  },
  borderRadius: "16px",
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fafbff 0%, #f8fafc 50%, #f1f5f9 100%);
`;

const OnboardingContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #f1f5f9;
  border-radius: ${theme.borderRadius};
  margin: 40px auto;
  width: 100%;
  max-width: 700px;
  padding: 40px;
  box-shadow: ${theme.shadows.lg};
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    margin: 24px auto;
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  h1 {
    margin: 0 0 10px 0;
    font-size: 34px;
    font-weight: 800;
    letter-spacing: 0.1px;
    color: ${theme.colors.textHeading};
    text-shadow: 0 1px 0 rgba(16, 24, 40, 0.04);

    @media (max-width: 768px) {
      font-size: 28px;
    }

    @media (max-width: 480px) {
      font-size: 24px;
      letter-spacing: 0px;
    }
  }
  
  p {
    margin: 0;
    font-size: 16px;
    color: ${theme.colors.textMuted};
    line-height: 1.5;

    @media (max-width: 768px) {
      font-size: 15px;
    }

    @media (max-width: 480px) {
      font-size: 14px;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 32px;
  
  h2 {
    margin: 0 0 16px 0;
    font-size: 18px;
    font-weight: 700;
    color: ${theme.colors.textHeading};
    letter-spacing: -0.02em;
  }
  
  p {
    margin: 0 0 20px 0;
    font-size: 14px;
    color: ${theme.colors.textMuted};
    line-height: 1.4;
  }
`;

const ProfessionalQuestion = styled.div`
  background: ${theme.colors.background};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  border: 1px solid #f1f5f9;
  
  h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.textHeading};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
  
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: ${theme.colors.textBody};
    font-weight: 500;
    
    input[type="radio"] {
      margin: 0;
      accent-color: ${theme.colors.primary};
    }
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
  }
`;

const CategoryCard = styled.div`
  background: ${props => props.selected ? theme.colors.primaryLight : theme.colors.surface};
  border: 2px solid ${props => props.selected ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  .name {
    font-weight: 600;
    color: ${theme.colors.textHeading};
    font-size: 14px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    gap: 12px;
  }
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  font-size: 14px;
  cursor: pointer;
  padding: 12px 0;
  font-weight: 500;
  
  &:hover {
    color: ${theme.colors.textBody};
    text-decoration: underline;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.errorLight};
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  color: ${theme.colors.error};
  font-size: 14px;
  font-weight: 500;
`;

/* =============================== Component =============================== */

const Onboarding = ({ state, dispatch }) => {
  const navigate = useNavigate();
  const [isProfessional, setIsProfessional] = useState(null);
  const [zipcode, setZipcode] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoading(true);
      const response = await request("POST", API_CONSTANT_MAP.fetch_categories);
      if (response.data?.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error("Failed to load categories:", error);
      setError("Failed to load categories. Please refresh the page.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      // Call backend to record skip
      const response = await request("POST", API_CONSTANT_MAP.process_onboarding, { action: "skip" });
      
      if (response.data?.success) {
        // Update Redux store with new onboarding status
        dispatch({ type: "UPDATE_ONBOARDING_STATUS", payload: "skipped" });
      }
      
      // Navigate regardless of response
      const urlParams = new URLSearchParams(window.location.search);
      const redirectTo = urlParams.get('redirect');
      navigate(redirectTo || "/");
    } catch (error) {
      console.error("Failed to record skip:", error);
      // Still navigate even if recording skip fails
      const urlParams = new URLSearchParams(window.location.search);
      const redirectTo = urlParams.get('redirect');
      navigate(redirectTo || "/");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isProfessional && !zipcode.trim()) {
      setError("Please enter your zipcode to receive notifications for tasks in your area.");
      return;
    }

    if (isProfessional && selectedCategories.length === 0) {
      setError("Please select at least one category you're interested in.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Call backend to process onboarding
      const onboardingData = {
        is_professional: isProfessional,
        zipcode: isProfessional ? zipcode.trim() : null,
        selected_categories: isProfessional ? selectedCategories : [],
        keywords: isProfessional && keywords.trim() ? keywords.trim().split(',').map(k => k.trim()) : []
      };

      const response = await request("POST", API_CONSTANT_MAP.process_onboarding, onboardingData);
      
      if (response.data?.success) {
        // Update Redux store with new onboarding status
        dispatch({ type: "UPDATE_ONBOARDING_STATUS", payload: "completed" });
        
        // Success - navigate to main page or redirect URL
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirect');
        navigate(redirectTo || "/");
      } else {
        setError(response.data?.error?.message || "Failed to save your preferences. Please try again.");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      setError(error.error?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = isProfessional === false || (isProfessional === true && zipcode.trim() && selectedCategories.length > 0);

  return (
    <Meta pageMeta={{ title: "Welcome to Doitly - Set up your preferences" }}>
      <NavBar user={state.user} />
      <PageWrapper>
        <OuterContainer>
          <OnboardingContainer>
            <Header>
              <h1>Welcome to Doitly</h1>
              <p>Set up your notification preferences to find the perfect opportunities</p>
            </Header>

            <ProfessionalQuestion>
              <h3>Are you looking to earn money by completing tasks for others?</h3>
              <RadioGroup>
                <label>
                  <input
                    type="radio"
                    name="professional"
                    value="yes"
                    checked={isProfessional === true}
                    onChange={() => setIsProfessional(true)}
                  />
                  Yes, I want to find work opportunities
                </label>
                <label>
                  <input
                    type="radio"
                    name="professional"
                    value="no"
                    checked={isProfessional === false}
                    onChange={() => setIsProfessional(false)}
                  />
                  No, I'm here to post tasks for others
                </label>
              </RadioGroup>
            </ProfessionalQuestion>

            {isProfessional === true && (
              <>
                <Section>
                  <h2>Your Location</h2>
                  <p>Enter your zipcode to get notifications for tasks in your area</p>
                  <TextInput
                    value={zipcode}
                    name="zipcode"
                    label="Zipcode"
                    onChange={(e) => setZipcode(e.target.value)}
                    placeholder="e.g., 94568"
                  />
                </Section>

                <Section>
                  <h2>Categories You're Interested In</h2>
                  <p>Select the types of tasks you'd like to be notified about</p>
                  
                  {isLoading ? (
                    <p>Loading categories...</p>
                  ) : (
                    <CategoryGrid>
                      {categories.map(category => (
                        <CategoryCard
                          key={category.id}
                          selected={selectedCategories.includes(category.id)}
                          onClick={() => handleCategorySelect(category.id)}
                        >
                          <div className="name">{category.name}</div>
                        </CategoryCard>
                      ))}
                    </CategoryGrid>
                  )}
                </Section>

                <Section>
                  <h2>Keywords (Optional)</h2>
                  <p>Add keywords to get more targeted notifications (comma-separated)</p>
                  <TextInput
                    value={keywords}
                    name="keywords"
                    label="Keywords"
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="e.g., urgent, same day, weekend"
                  />
                </Section>
              </>
            )}

            {isProfessional === false && (
              <Section>
                <p>Perfect! You're all set to start posting tasks and finding great service providers.</p>
              </Section>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ActionButtons>
              <SkipButton onClick={handleSkip}>
                Skip for now
              </SkipButton>
              
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                style={{
                  background: canSubmit ? `linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%)` : "#94a3b8",
                  borderRadius: "12px",
                  fontWeight: 700,
                  padding: "12px 32px",
                  opacity: isSubmitting ? 0.7 : 1,
                  border: "none",
                  color: theme.colors.surface,
                  fontSize: "14px",
                  cursor: canSubmit && !isSubmitting ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease",
                }}
              >
                {isProfessional === true ? 
                  (isSubmitting ? "Setting up..." : "Set up notifications") :
                  (isSubmitting ? "Finishing..." : "Continue")
                }
              </Button>
            </ActionButtons>
          </OnboardingContainer>
        </OuterContainer>
      </PageWrapper>
    </Meta>
  );
};

const mapStateToProps = (state) => {
  return { state };
};

export default connect(mapStateToProps)(Onboarding);